import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Zap, Building2, CircleSlash, ArrowRight, Radio, LogOut, IndianRupee, Layers, Link2, Search, Wand2, BadgeCheck, Plug, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/App";
import IntegrationsTab from "@/components/workspace/IntegrationsTab";

const STATUS_BADGE = {
  draft: ["Setup in progress", "bg-amber-50 text-amber-600 border-amber-200"],
  payment_pending: ["Payment pending", "bg-sky-50 text-sky-600 border-sky-200"],
  active: ["PMS Active", "bg-emerald-50 text-emerald-600 border-emerald-200"],
};

const CARD_ACCENTS = ["#4F46E5", "#0EA5E9", "#8B5CF6", "#10B981", "#F59E0B", "#EC4899"];

export default function PmsHome() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [poolQuery, setPoolQuery] = useState("");
  const [form, setForm] = useState({ name: "", city: "", unit_count: "3", category: "homestay" });

  const load = () => api.get("/pms/overview").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/pms/activate");
      toast.success(`PMS activated — ${r.listings_pulled} Atiti listings pulled into your pool`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const createProperty = async () => {
    if (!form.name.trim()) return toast.error("Give the property a name");
    const n = parseInt(form.unit_count, 10);
    if (!n || n < 1 || n > 500) return toast.error("Enter a unit count between 1 and 500");
    setBusy(true);
    try {
      const { data: p } = await api.post("/pms/properties", { name: form.name, city: form.city, unit_count: n, category: form.category });
      toast.success(`${p.name} created with ${n} units`);
      nav(`/pms/property/${p.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const skip = async (lid) => {
    try { await api.post(`/pms/pool/${lid}/skip`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const loadDemo = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/pms/demo-seed");
      toast.success(`Demo portfolio loaded — ${r.properties} properties, ${r.units} units`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const seedMoreListings = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/pms/seed-more-listings", { count: 10 });
      await api.post("/pms/refresh-pool");
      toast.success(`${r.added} fresh demo listings dropped into your pool — go connect them`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const refreshFromOta = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/pms/refresh-pool");
      const parts = [];
      if (r.added) parts.push(`+${r.added} new`);
      if (r.updated) parts.push(`~${r.updated} updated`);
      if (r.removed) parts.push(`−${r.removed} removed`);
      if (r.stale) parts.push(`${r.stale} stale`);
      if (parts.length === 0) {
        toast.success(r.total_on_ota
          ? `In sync — ${r.total_on_ota} listing${r.total_on_ota === 1 ? "" : "s"} on your Atiti OTA account`
          : "No listings on your Atiti OTA account yet. Add a listing on the Atiti app, then tap Sync again.");
      } else {
        toast.success(`Synced from Atiti OTA — ${parts.join(" · ")} · ${r.total_on_ota} total on OTA`);
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={32} /></div>;

  const available = data.pool.filter((p) => p.status === "available");
  const skipped = data.pool.filter((p) => p.status === "skipped");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="backdrop-blur-xl bg-white/90 border-b border-slate-200/80 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="back-to-home-button" onClick={() => nav("/")} className="text-slate-400 hover:text-slate-700"><ArrowLeft size={18} /></button>
            <Logo h={32} tag="PMS" />
          </div>
          <div className="flex items-center gap-3">
            {data?.properties?.some((p) => p.status === "active") && (
              <button data-testid="launch-pms-button" onClick={() => nav("/pms/launch")}
                className="btn-brand rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5">
                <Zap size={13} /> Launch PMS <ArrowRight size={12} />
              </button>
            )}
            {data?.activated && data?.sync && (
              <span data-testid="atiti-listing-count-pill"
                className="hidden sm:inline-flex px-3 py-1.5 rounded-full bg-white border border-slate-200 font-semibold text-xs text-slate-600 items-center gap-1.5"
                title="Total listings on your Atiti OTA account synced into PMS">
                <span className={`w-1.5 h-1.5 rounded-full ${data.sync.total_on_ota === data.sync.total_in_pool ? "bg-emerald-400" : "bg-amber-400"}`} />
                {data.sync.total_on_ota} Atiti listing{data.sync.total_on_ota === 1 ? "" : "s"}
              </span>
            )}
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <span className="text-xs text-slate-400 hidden md:inline">{user?.email}</span>
              <button data-testid="logout-button" onClick={() => { logout(); nav("/"); }}
                className="text-slate-400 hover:text-slate-700 flex items-center gap-1 text-xs"><LogOut size={13} /> Log out</button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!data.activated ? (
          <div className="max-w-xl mx-auto text-center mt-16 reveal">
            <div className="w-16 h-16 rounded-2xl bg-brand text-white flex items-center justify-center mx-auto mb-6 shadow-xl shadow-pink-200"><Zap size={28} /></div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Activate Atiti PMS</h1>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6 text-left">
              <p className="text-[11px] font-bold uppercase tracking-wider brand-pink">Step 1 — mandatory</p>
              <p className="text-sm text-slate-600 mt-1.5">Your listings must exist on the <b>Atiti app</b> first. List your spaces there, and PMS pulls everything in automatically.</p>
              <div className="flex items-center gap-3 mt-4">
                <a data-testid="playstore-badge" href="https://play.google.com/store/apps/details?id=com.atititi.app" target="_blank" rel="noopener noreferrer">
                  <img src="https://nammayatri.in/logos/playStore.svg" alt="Get it on Google Play" className="h-10 hover:scale-[1.03] transition-transform" />
                </a>
                <a data-testid="appstore-badge" href="https://apps.apple.com/app/id6772948860" target="_blank" rel="noopener noreferrer">
                  <img src="https://nammayatri.in/logos/appStore.svg" alt="Download on the App Store" className="h-10 hover:scale-[1.03] transition-transform" />
                </a>
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-5 leading-relaxed">Already listed? We'll pull every listing from your Atiti account into one place. You'll then group them into properties, define units, and connect channels — property by property.</p>
            <button data-testid="activate-pms-button" onClick={activate} disabled={busy}
              className="btn-brand rounded-full px-8 py-3.5 text-sm font-semibold mt-8 inline-flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />} Activate & pull my listings
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 reveal">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Portfolio</p>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">Your properties</h1>
                <p className="text-sm text-slate-500 mt-1.5">Each property is set up, paid for, and activated independently — homestays &amp; vacation rentals bill at <b>₹475/unit</b>, hotels &amp; hostels at <b>₹300/unit</b>. Same rate as the OTA.</p>
              </div>
              <div className="flex items-center gap-2">
                {data.properties.length < 5 && (
                  <button data-testid="load-demo-button" onClick={loadDemo} disabled={busy}
                    className="rounded-full px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border border-slate-300 text-slate-600 hover:border-slate-500 hover:text-slate-800 transition-colors bg-white">
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Wand2 size={15} />} Load demo portfolio
                  </button>
                )}
                <button data-testid="create-property-button" onClick={() => setShowCreate(true)}
                  className="btn-brand rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Plus size={16} /> New property</button>
              </div>
            </div>

            {data.properties.length > 0 && (() => {
              const totalUnits = data.properties.reduce((s, p) => s + p.unit_count, 0);
              const activeUnits = data.properties.reduce((s, p) => s + (p.active_units || 0), 0);
              const monthly = activeUnits * 475;
              const stats = [
                { icon: Building2, label: "Properties", value: data.properties.length, testid: "stat-properties" },
                { icon: Layers, label: "Total units", value: totalUnits, testid: "stat-units" },
                { icon: BadgeCheck, label: "Active subscriptions", value: activeUnits, testid: "stat-active" },
                { icon: IndianRupee, label: "Monthly subscription", value: `₹${monthly.toLocaleString("en-IN")}`, testid: "stat-monthly" },
              ];
              return (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-7">
                  {stats.map(({ icon: Icon, label, value, testid }) => (
                    <div key={label} data-testid={testid} className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex items-center gap-3.5 reveal">
                      <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] flex items-center justify-center shrink-0"><Icon size={16} className="brand-pink" /></div>
                      <div className="min-w-0">
                        <p className="font-display font-extrabold text-xl text-slate-900 leading-tight truncate">{value}</p>
                        <p className="text-[11px] text-slate-400 font-medium truncate">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-7">
              {data.properties.map((p, i) => {
                const [label, cls] = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                const accent = CARD_ACCENTS[i % CARD_ACCENTS.length];
                return (
                  <button key={p.id} data-testid={`property-card-${p.id}`} onClick={() => nav(`/pms/property/${p.id}`)}
                    className="text-left bg-white rounded-3xl border border-slate-200 overflow-hidden group hover:shadow-xl hover:shadow-slate-200/80 hover:-translate-y-0.5 transition-all reveal">
                    <div className="relative bg-slate-900 px-6 pt-5 pb-4">
                      <span className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: accent }} />
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-display font-bold text-lg text-white truncate">{p.name}</h3>
                          <p className="text-xs text-slate-400 mt-0.5">{p.city || "—"} · {p.unit_count} units</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                          {p.channels_live && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-violet-50 text-violet-600 border-violet-200 flex items-center gap-1"><Radio size={9} /> Live</span>}
                        </div>
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {Array.from({ length: Math.min(p.unit_count, 24) }).map((_, j) => (
                          <span key={j} className={`w-2.5 h-2.5 rounded-[4px] ${j < (p.active_units || 0) ? "bg-emerald-400" : "bg-slate-200"}`} />
                        ))}
                        {p.unit_count > 24 && <span className="text-[10px] text-slate-400 ml-1">+{p.unit_count - 24}</span>}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1.5">{p.active_units || 0} of {p.unit_count} units on active subscription</p>
                      <div className="flex items-center gap-4 mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3.5">
                        <span className="flex items-center gap-1.5"><Link2 size={12} className="text-slate-400" /> {p.mapped || 0} mapped</span>
                        <span className="flex items-center gap-1.5"><Radio size={12} className="text-slate-400" /> {p.channel_count || 0} channels</span>
                        <span className="flex items-center gap-1.5 ml-auto font-semibold text-slate-700"><IndianRupee size={11} /> {((p.active_units || 0) * 475).toLocaleString("en-IN")}/mo</span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold brand-pink mt-3.5 group-hover:gap-2 transition-all">{p.status === "active" ? "Open channels & details" : "Continue setup"} <ArrowRight size={13} /></span>
                    </div>
                  </button>
                );
              })}
              {data.properties.length === 0 && (
                <div className="md:col-span-2 xl:col-span-3 border-2 border-dashed border-slate-200 rounded-3xl p-14 text-center reveal">
                  <div className="w-14 h-14 rounded-2xl bg-[#EEF2FF] flex items-center justify-center mx-auto"><Building2 size={24} className="brand-pink" /></div>
                  <h3 className="font-display font-bold text-slate-900 mt-4">No properties yet</h3>
                  <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">Create your first property to start mapping listings — or load a 5-property demo portfolio to see the PMS fully populated.</p>
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button data-testid="empty-demo-button" onClick={loadDemo} disabled={busy}
                      className="rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 border border-slate-300 text-slate-600 hover:border-slate-500 bg-white">
                      {busy ? <Loader2 className="animate-spin" size={15} /> : <Wand2 size={15} />} Load demo portfolio
                    </button>
                    <button data-testid="empty-create-button" onClick={() => setShowCreate(true)}
                      className="btn-brand rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Plus size={15} /> New property</button>
                  </div>
                </div>
              )}
            </div>

            <section className="mt-12 reveal">
              <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-lg font-bold text-slate-900">Atiti listing pool</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Listings deplete across properties as you map them. Skip test/inactive listings you don't want in PMS.</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">{data.pool.filter((l) => l.status === "assigned").length} mapped</span>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-500">{available.length} available</span>
                  {skipped.length > 0 && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-400">{skipped.length} skipped</span>}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                  <Search size={14} className="text-slate-400 shrink-0" />
                  <input data-testid="pool-search-input" value={poolQuery} onChange={(e) => setPoolQuery(e.target.value)}
                    placeholder="Search listings by name, city or ID…"
                    className="w-full bg-transparent text-sm focus:outline-none placeholder:text-slate-400" />
                </div>
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {data.pool
                    .filter((l) => !poolQuery.trim() || `${l.title} ${l.city} ${l.listing_id}`.toLowerCase().includes(poolQuery.trim().toLowerCase()))
                    .map((l) => (
                      <div key={l.listing_id} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/70 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-xs text-slate-400 w-16 shrink-0">{l.listing_id.slice(0, 8)}</span>
                          <span className={`text-sm truncate ${l.status === "skipped" ? "text-slate-400 line-through" : "text-slate-700"}`}>{l.title}</span>
                          <span className="text-xs text-slate-400 shrink-0">{l.city}</span>
                          {l.total_inventory ? <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 shrink-0">{l.total_inventory} rooms</span> : null}
                          {l.stale && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 shrink-0" title="This listing was removed on the Atiti OTA but is still mapped in PMS. Unmap it if you no longer sell it.">deleted on OTA</span>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          {l.status === "assigned" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">mapped</span>}
                          {l.status === "skipped" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">not in PMS</span>}
                          {l.status !== "assigned" && (
                            <button data-testid={`skip-listing-${l.listing_id}`} onClick={() => skip(l.listing_id)}
                              className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1"><CircleSlash size={12} /> {l.status === "skipped" ? "restore" : "skip"}</button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {showCreate && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-7 max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl font-bold text-slate-900">New property</h3>
            <p className="text-xs text-slate-500 mt-1">Units are created as named slots first (A, B, C…). Listings are mapped later.</p>
            <div className="space-y-4 mt-5">
              <input data-testid="property-name-input" placeholder="Property name (e.g. Casa Baga, Goa)" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
              <input data-testid="property-city-input" placeholder="City" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
              <div>
                <label className="text-xs font-semibold text-slate-500">Property category</label>
                <p className="text-[11px] text-slate-400 mt-0.5">Hotel-type properties bill at ₹300/unit and auto-map every unit to one listing. Homestay/vacation rental bill at ₹475/unit with one listing per unit.</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { key: "homestay", label: "Homestay / Vacation Rental", desc: "Villa · Apartment · Bungalow · etc.", rate: "₹475" },
                    { key: "hotel", label: "Hotel-type", desc: "Hotel · Motel · Hostel · Dormitory", rate: "₹300" },
                  ].map((c) => (
                    <button key={c.key} type="button"
                      data-testid={`property-category-${c.key}`}
                      onClick={() => setForm({ ...form, category: c.key })}
                      className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${form.category === c.key ? "border-[#4F46E5] bg-[#EEF2FF]" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                      <p className="text-sm font-semibold text-slate-900">{c.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{c.desc}</p>
                      <p className="text-[11px] font-semibold brand-pink mt-1">{c.rate} / unit / mo</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Number of units</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <button data-testid="units-decrement-button" type="button"
                    onClick={() => setForm((f) => ({ ...f, unit_count: String(Math.max(1, (parseInt(f.unit_count, 10) || 1) - 1)) }))}
                    className="w-10 h-10 rounded-lg border border-slate-300 text-slate-600 font-bold text-lg hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors">−</button>
                  <input data-testid="property-units-input" type="text" inputMode="numeric" pattern="[0-9]*" value={form.unit_count}
                    onChange={(e) => setForm({ ...form, unit_count: e.target.value.replace(/\D/g, "").slice(0, 3) })}
                    placeholder="e.g. 10"
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
                  <button data-testid="units-increment-button" type="button"
                    onClick={() => setForm((f) => ({ ...f, unit_count: String(Math.min(500, (parseInt(f.unit_count, 10) || 0) + 1)) }))}
                    className="w-10 h-10 rounded-lg border border-slate-300 text-slate-600 font-bold text-lg hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors">+</button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">No limit — add 10, 20, 40… as many as the property has. You can rename every unit later; your name becomes its primary identity across all channels.</p>
              </div>
            </div>
            <button data-testid="confirm-create-property-button" onClick={createProperty} disabled={busy}
              className="btn-brand w-full rounded-full py-3 text-sm font-semibold mt-6 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Create property
            </button>
          </div>
        </div>
      )}
      {connectOpen && (
        <IntegrationsTab
          pid={data?.properties?.find((p) => p.status === "active")?.id || data?.properties?.[0]?.id}
          propertyName={data?.properties?.find((p) => p.status === "active")?.name || data?.properties?.[0]?.name || "Your portfolio"}
          onClose={() => setConnectOpen(false)}
        />
      )}
    </div>
  );
}
